import React from 'react';

export type StickyProps = {
    /**
     * The children to render.
     */
    children: React.ReactElement;
    /**
     * Whether the sticky behavior is disabled.
     */
    disabled?: boolean;
    /**
     * The ref for the intersection observer.
     */
    intersectionRef: React.Ref<HTMLDivElement>;
    /**
     * The ref for the sticky element.
     */
    elementRef: React.Ref<HTMLDivElement>;
    /**
     * Custom styles to apply to the sticky element wrapper.
     */
    targetStyles: React.CSSProperties;
    /**
     * Custom styles to apply to the placeholder element.
     */
    placeholderStyles: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * Sticky component that makes its children stick to the viewport when scrolled out of view.
 * Use this when CSS `position: sticky` doesn't fit your needs, such as when:
 * - You need to dynamically modify props when the element becomes sticky
 * - The width of the sticky element needs to become bigger than its original container (e.g. when using `position: fixed`)
 * - You have trouble with the sticky not working because of parent elements with `overflow: hidden`
 *
 * The `Sticky` should be used as a wrapper around the element you want to make sticky, the main logic is handled by the `useSticky` hook.
 *
 * ## Key Features
 * - Maintains document flow when elements enter sticky state avoiding layout jumps
 * - Automatically adjusts for scrollbar presence
 * - Easily apply different styles/props when the element is sticky
 * - Can be enabled/disabled dynamically via the `disabled` prop
 * - Preserves original element semantics and attributes
 *
 * ## Implementation
 * Uses an empty div to detect when the top of the element scrolls out of view, then
 * creates a placeholder with the exact height of the sticky element to prevent
 * content jumps, while applying custom styling for positioning.
 *
 * @example
 * ```tsx
 *   const sticky = useSticky();
 *   <Sticky {...sticky}>
 *     <Header>The header is sticky: {sticky.isSticky ? 'Yes' : 'No'}</Header>
 *   </Sticky>
 * ```
 */
export const Sticky = ({
    intersectionRef,
    elementRef,
    disabled = false,
    children,
    targetStyles,
    placeholderStyles,
    ...rest
}: StickyProps): React.ReactElement => {
    if (disabled) {
        return children;
    }

    return (
        <div {...rest}>
            {/* reference element, when out of view, the children will become sticky */}
            <div ref={intersectionRef} />
            {/* placeholder to prevent layout jumps when element becomes sticky */}
            <div style={placeholderStyles} />
            {/* sticky element */}
            <div ref={elementRef} style={targetStyles}>
                {children}
            </div>
        </div>
    );
};
